import { useCallback, useMemo } from 'react';

import classnames from 'classnames';
import omit from 'lodash/omit';

import { CATEGORIES, COMPONENTS, SECTIONS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import ArtistProfessionDropdown from 'components/Globals/FormFields/Display/ArtistProfessionDropdown';

import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';

import { ENTITY_TYPES, FILTER_TYPES, PROFESSION_TYPES } from 'operabase-router/constants';

import classes from './ArtistProfessionFilters.module.scss';

const artistProfessionTrackingData = {
  section: SECTIONS.ARTIST_LIST,
  component: COMPONENTS.FILTERS,
  category: CATEGORIES.SEARCH,
};

// Helper function to determine profession type from API data
const getProfessionType = profession => {
  if (profession?.type === PROFESSION_TYPES.VOICE_TYPE) {
    return PROFESSION_TYPES.VOICE_TYPE;
  }
  if (profession?.type === PROFESSION_TYPES.INSTRUMENTALIST) {
    return PROFESSION_TYPES.INSTRUMENTALIST;
  }
  return PROFESSION_TYPES.PROFESSION;
};

// Helper function to show value only if it matches the dropdown type
const getSelectedValueByProfessionType = (selectedProfession, dropdownTypes) => {
  if (!selectedProfession) return null;
  
  const professionType = getProfessionType(selectedProfession);
  return dropdownTypes.includes(professionType) ? selectedProfession : null;
};

const ArtistProfessionFilters = ({ className }) => {
  const { obRouteContext } = useAppContext();
  const { applyFilters } = usePageContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps || {};

  const professionFields = useMemo(
    () => [
      {
        types: [PROFESSION_TYPES.PROFESSION],
        trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.PROFESSIONS },
      },
      {
        types: [PROFESSION_TYPES.VOICE_TYPE],
        trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.VOICE_TYPES },
      },
      {
        types: [PROFESSION_TYPES.INSTRUMENTALIST],
        trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.INSTRUMENTALISTS },
      },
      {
        types: [PROFESSION_TYPES.ROLE_REPERTOIRE],
        trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.VOICE_TYPES },
        isRoleRepertoire: true,
      },
    ],
    [],
  );

  const onSelectProfession = useCallback(
    (e, profession) => {
      const professionFilterKey = FILTER_TYPES.PROFESSION;
      const pageFilterKey = FILTER_TYPES.PAGE;
      const pageFilterCurrentValue = filters?.[pageFilterKey];
      const finalFiltersExcludingPage = omit(filters, [professionFilterKey, pageFilterKey]);

      if (profession) {
        finalFiltersExcludingPage[FILTER_TYPES.PROFESSION] = [profession];
      }

      applyFilters({
        entityType: ENTITY_TYPES.PROFILE,
        filters: {
          ...finalFiltersExcludingPage,
          ...(pageFilterCurrentValue && Object.keys(finalFiltersExcludingPage).length > 0
            ? { [pageFilterKey]: 1 }
            : {}),
        },
      });
    },
    [applyFilters, filters],
  );

  const selectedProfession = useMemo(() => filters?.[FILTER_TYPES.PROFESSION]?.[0]?.entity || null, [filters]);

  return (
    <div className={classnames(classes.root, { [className]: !!className })}>
      {professionFields?.map((fieldProps, index) => (
        <ArtistProfessionDropdown
          key={index}
          className={classes.professionDropdown}
          value={getSelectedValueByProfessionType(selectedProfession, fieldProps.types)}
          onChange={onSelectProfession}
          variant="standard"
          {...fieldProps}
        />
      ))}
    </div>
  );
};

export default ArtistProfessionFilters;
